import { useEffect, useState } from 'react'
import { Button, Form, Modal } from 'react-bootstrap'

import getMarketplaceDesc from '../../../modules/redux/sales/getMarketplaceDesc'
import updateMarketplaceDesc from '../../../modules/redux/sales/updateMarketplaceDesc'
import { useDispatch } from '../../../modules/store/customDispatch'
import Loader from '../../_atoms/Loader/Loader'
import OverlayWithText from '../../_atoms/OverlayWithText/OverlayWithText'
import SalesDescriptionInput from '../SalesDescriptionInput'
import MarketplaceDescScheme, { DescBlockId, defaultDescOrder, normalizeDescOrder } from './MarketplaceDescScheme'
import styles from './styles.module.scss'

const DESC_LIMIT = 4000

interface IProps {
    isOpen: boolean
    closeModal: () => void
    marketplace: string
    title: string
}

const ModalMarketplaceDesc = ({ isOpen, closeModal, marketplace, title }: IProps) => {
    const dispatch = useDispatch()
    const [prefix, setPrefix] = useState('')
    const [suffix, setSuffix] = useState('')
    const [order, setOrder] = useState<DescBlockId[]>(() => defaultDescOrder(marketplace))
    const [savedPrefix, setSavedPrefix] = useState('')
    const [savedSuffix, setSavedSuffix] = useState('')
    const [savedOrder, setSavedOrder] = useState<DescBlockId[]>(() => defaultDescOrder(marketplace))
    const [isLoading, setIsLoading] = useState(false)
    const [isLoaded, setIsLoaded] = useState(false)
    const [error, setError] = useState('')

    useEffect(() => {
        let cancelled = false
        const load = async () => {
            const fallback = defaultDescOrder(marketplace)
            setOrder(fallback)
            setSavedOrder(fallback)
            setIsLoading(true)
            setError('')
            const data = await dispatch(getMarketplaceDesc(marketplace))
            if (cancelled) {
                return
            }
            if (!data) {
                setError('Не удалось загрузить описание')
                setIsLoading(false)
                return
            }
            const nextOrder = normalizeDescOrder(data.order, marketplace)
            setPrefix(data.prefix)
            setSuffix(data.suffix)
            setOrder(nextOrder)
            setSavedPrefix(data.prefix)
            setSavedSuffix(data.suffix)
            setSavedOrder(nextOrder)
            setIsLoaded(true)
            setIsLoading(false)
        }
        load()
        return () => {
            cancelled = true
        }
    }, [marketplace])

    const overLimit = prefix.length > DESC_LIMIT || suffix.length > DESC_LIMIT
    const unchanged = prefix === savedPrefix && suffix === savedSuffix && order.join('|') === savedOrder.join('|')

    const moveBlock = (from: number, to: number) => {
        if (from === to || from < 0 || to < 0 || from >= order.length || to >= order.length) {
            return
        }
        const next = order.slice()
        const [item] = next.splice(from, 1)
        next.splice(to, 0, item)
        setOrder(next)
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (!isLoaded || overLimit || unchanged) {
            return
        }
        setIsLoading(true)
        setError('')
        const saved = await dispatch(updateMarketplaceDesc(marketplace, { prefix, suffix, order }))
        setIsLoading(false)
        if (!saved) {
            setError('Не удалось сохранить описание')
            return
        }
        closeModal()
    }

    return (
        <Modal show={isOpen} onHide={closeModal} centered size="lg">
            {isLoading && (
                <OverlayWithText backgroundBootstrapColor={'bg-secondary'}>
                    <Loader diameterInPx={100} thicknessInPx={10} />
                </OverlayWithText>
            )}
            <Modal.Body>
                <Form onSubmit={handleSubmit}>
                    <h4>
                        <b>Дополнительное описание — {title}</b>
                    </h4>
                    <div className={styles.layout}>
                        <div>
                            <SalesDescriptionInput
                                title="Текст в начало описания"
                                value={prefix}
                                onChangeTextArea={setPrefix}
                                errorMessage="Превышен лимит"
                                textareaMaxLength={DESC_LIMIT}
                                width="100%"
                                isDisabled={!isLoaded}
                            />
                            <SalesDescriptionInput
                                title="Текст в конец описания"
                                value={suffix}
                                onChangeTextArea={setSuffix}
                                errorMessage="Превышен лимит"
                                textareaMaxLength={DESC_LIMIT}
                                width="100%"
                                marginTopPixels={8}
                                isDisabled={!isLoaded}
                            />
                        </div>
                        <MarketplaceDescScheme order={order} onMove={moveBlock} />
                    </div>
                    {error && <div className="text-danger mb-2">{error}</div>}
                    <div className="d-flex justify-content-end mt-3">
                        <Button type="submit" disabled={!isLoaded || overLimit || unchanged || isLoading}>
                            Сохранить
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    )
}

export default ModalMarketplaceDesc
